import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useApiContext from 'hooks/useApiContext';
import { iCheckpointScanInfo } from 'services/Ticketing/Ticketing.type';
import ScannerView, { ScannerApi } from './ScannerView';
import { formatShortDay } from './common';

/** The PIN proof stays on the device; it dies when the PIN or URL changes. */
const sessionStore = {
  key: (token: string) => `em.checkpoint.${token}.session`,
  get(token: string): string | null {
    try {
      return localStorage.getItem(this.key(token));
    } catch {
      return null;
    }
  },
  set(token: string, value: string | null): void {
    try {
      if (value) localStorage.setItem(this.key(token), value);
      else localStorage.removeItem(this.key(token));
    } catch {
      /* ignore */
    }
  },
};

const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#030712',
    color: '#fff',
    fontFamily: 'Inter, Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box',
  },
  box: { width: '100%', maxWidth: 360, textAlign: 'center' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 12,
    background: 'rgba(255,255,255,.1)',
    border: '1px solid rgba(255,255,255,.25)',
    padding: '14px 12px',
    color: '#fff',
    fontSize: 28,
    letterSpacing: 12,
    textAlign: 'center',
  },
  button: {
    width: '100%',
    marginTop: 16,
    padding: '14px 16px',
    borderRadius: 12,
    background: '#0d9488',
    color: '#fff',
    border: 0,
    fontSize: 18,
    fontWeight: 600,
    cursor: 'pointer',
  },
};

function Message({
  title,
  text,
}: {
  title: string;
  text?: string;
}): JSX.Element {
  return (
    <div style={S.page}>
      <div style={S.box}>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{title}</div>
        {text && (
          <p style={{ color: 'rgba(255,255,255,.7)', marginTop: 8 }}>{text}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Scanner of an entrance checkpoint, opened from its secret URL (or QR code)
 * by hosts and temporary staff who have no EM account.
 */
export default function CheckpointScanner(): JSX.Element {
  const { token = '' } = useParams();
  const { ticketingRepository } = useApiContext();
  const [session, setSession] = useState<string | null>(() =>
    sessionStore.get(token)
  );
  const [info, setInfo] = useState<iCheckpointScanInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const load = useCallback(
    async (s: string | null) => {
      try {
        setInfo(await ticketingRepository.getCheckpointInfo(token, s));
        setError(null);
      } catch (e: any) {
        setError(e.message);
      }
    },
    [token, ticketingRepository]
  );

  useEffect(() => {
    load(session);
  }, [load]);

  const unlock = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setUnlocking(true);
    setPinError(null);
    try {
      const r = await ticketingRepository.unlockCheckpoint(token, pin);
      sessionStore.set(token, r.session);
      setSession(r.session);
      setPin('');
      await load(r.session);
    } catch (err: any) {
      setPinError(err.message);
    } finally {
      setUnlocking(false);
    }
  };

  const api = useMemo<ScannerApi>(
    () => ({
      storageKey: `em.checkpoint.${token}`,
      scan: async (code, device) =>
        await ticketingRepository.checkpointScanRequest(
          token,
          session,
          code,
          device
        ),
      sync: async (scans, device) =>
        await ticketingRepository.checkpointSync(token, session, scans, device),
      offline: async () =>
        await ticketingRepository.checkpointOfflineList(token, session),
    }),
    [token, session, ticketingRepository]
  );

  const onRejected = useCallback(
    (status: number, message: string) => {
      if (status === 401) {
        // PIN changed by the organiser: ask for it again
        sessionStore.set(token, null);
        setSession(null);
        setPinError('Le code PIN a changé. Saisissez le nouveau code.');
        load(null);
        return;
      }
      if (status === 403) {
        load(session);
        return;
      }
      setError(message);
    },
    [token, session, load]
  );

  if (error) return <Message title="Scan impossible" text={error} />;
  if (!info) return <div style={S.page} />;

  const cp = info.checkpoint;
  const needsPin = cp.requires_pin && info.counters === null;
  if (needsPin)
    return (
      <div style={S.page}>
        <form style={S.box} onSubmit={unlock}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>
            {info.event.name}
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, margin: '4px 0 24px' }}>
            {cp.name}
          </div>
          <label
            htmlFor="checkpoint-pin"
            style={{ display: 'block', marginBottom: 8 }}
          >
            Code PIN donné par l&apos;organisateur
          </label>
          <input
            id="checkpoint-pin"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            style={S.input}
          />
          {pinError && (
            <p style={{ color: '#fca5a5', marginTop: 12 }}>{pinError}</p>
          )}
          <button
            type="submit"
            disabled={pin.length < 4 || unlocking}
            style={{ ...S.button, opacity: pin.length < 4 ? 0.5 : 1 }}
          >
            {unlocking ? 'Vérification…' : 'Ouvrir le scanner'}
          </button>
        </form>
      </div>
    );

  if (!cp.open_today) {
    const from = cp.valid_from ? formatShortDay(cp.valid_from) : null;
    const until = cp.valid_until ? formatShortDay(cp.valid_until) : null;
    return (
      <Message
        title={`${cp.name} : fermé aujourd'hui`}
        text={
          from !== null || until !== null
            ? `Ouvert ${from ? `du ${from}` : ''}${
                until ? ` au ${until}` : ''
              }.`
            : undefined
        }
      />
    );
  }

  return (
    <ScannerView
      api={api}
      title={info.event.name}
      subtitle={`${cp.name}${
        cp.all_types ? '' : ` · ${cp.accepted_types.join(', ')}`
      }`}
      timezone={info.timezone}
      eventDays={info.event.days ?? []}
      initialCounters={
        info.counters ?? {
          entries_today: 0,
          refused_today: 0,
          valid_tickets: 0,
          entries_here_today: 0,
        }
      }
      onRejected={onRejected}
    />
  );
}
